/**
 * RecordSelectionEditor — modal for viewing and editing an active action's state.
 *
 * Shows:
 * - Primary scope with PK count
 * - Secondary scope mappings from actionState.scopeMappings with status badges
 * - Remove/restore buttons for scope mappings
 * - Rollup/Rolldown placeholders (non-functional)
 * - Column visibility per scope
 * - Option to add another file or clear selection
 *
 * Falls back to legacy recordSelection display if no actionState exists.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import clsx from "clsx"
import { useRef, useState } from "react"
import { useStore } from "../../store/useStore"
import type { SelectMapping } from "../../types"

export function RecordSelectionEditor() {
  const { translate } = useTranslation()
  const {
    actionState,
    recordSelection,
    recordSelectionEditorOpen,
    closeRecordSelectionEditor,
    toggleSelectionPrimitive,
    toggleSelectionRollup,
    clearRecordSelection,
    removePkSet,
    setCrossScopeOperator,
    openIngestion,
    activeScopeId,
    removeScopeMapping,
    restoreScopeMapping,
    cancelAction,
    scopes,
  } = useStore()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [nearMissExpanded, setNearMissExpanded] = useState(false)
  const [activeTab, setActiveTab] = useState<string>(activeScopeId ?? "")

  if (!recordSelectionEditorOpen || !recordSelection) return null

  const { pkSets, mappings, nearMisses, crossScopeOperator } = recordSelection
  const visibleCount = mappings.filter((m) => m.visible).length
  const totalPks = pkSets.reduce((sum, ps) => sum + ps.pkValues.length, 0)

  // Group mappings by scope — current scope first
  const byScope = new Map<string, SelectMapping[]>()
  if (activeScopeId) byScope.set(activeScopeId, [])
  for (const m of mappings) {
    if (!byScope.has(m.scopeId)) byScope.set(m.scopeId, [])
    byScope.get(m.scopeId)!.push(m)
  }
  if (activeScopeId && byScope.get(activeScopeId)!.length === 0)
    byScope.delete(activeScopeId)

  const scopeIds = Array.from(byScope.keys())
  const effectiveTab = byScope.has(activeTab) ? activeTab : (scopeIds[0] ?? "")
  const tabMappings = byScope.get(effectiveTab) ?? []

  const handleAddFile = (file: File) => {
    closeRecordSelectionEditor()
    openIngestion(activeScopeId, file)
  }

  // Scope name lookup
  const scopeName = (id: string) => scopes.find((s) => s.id === id)?.name ?? id

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs">
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-grey2 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-grey4 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-white">
              {actionState
                ? translate("QMS/SELECTOR/ACTIVE_ACTION")
                : translate("QMS/SELECTOR/RECORD_SELECTION")}
            </h2>
            <p className="mt-0.5 text-xs text-grey5">
              {actionState ? (
                <>
                  {actionState.type === "select"
                    ? translate("QMS/SELECTOR/ACTION_TYPE_SELECT")
                    : actionState.type === "import"
                      ? translate("QMS/SELECTOR/ACTION_TYPE_IMPORT")
                      : translate("QMS/SELECTOR/ACTION_TYPE_EXPORT")}{" "}
                  — {scopeName(actionState.primaryScopeId)} —{" "}
                  {actionState.primaryPkValues.length ||
                    translate("QMS/SELECTOR/ALL_RECORDS_SHORT")}{" "}
                  PKs
                </>
              ) : (
                <>
                  {translate("QMS/SELECTOR/HEADER_SUMMARY", {
                    scopeCount: pkSets.length,
                    pkCount: totalPks,
                    visibleCount,
                  })}
                </>
              )}
            </p>
          </div>
          <button
            onClick={closeRecordSelectionEditor}
            className="ml-4 p-1 text-xl leading-none text-grey5 hover:text-white"
          >
            x
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Primary scope info */}
          {actionState && (
            <div className="mb-4">
              <h3 className="mb-2 text-sm font-medium text-grey6">
                {translate("QMS/SELECTOR/PRIMARY_SCOPE")}
              </h3>
              <div className="rounded-lg border border-grey4 bg-grey3 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-yellow1/15 px-1.5 py-0.5 text-[10px] font-medium text-yellow1">
                    {translate("QMS/ANCHOR/BADGE")}
                  </span>
                  <span className="text-xs font-medium capitalize text-white">
                    {scopeName(actionState.primaryScopeId)}
                  </span>
                  <span className="text-[10px] text-grey5">
                    {actionState.primaryPkValues.length > 0
                      ? translate("QMS/SELECTOR/PKS_SELECTED", {
                          count: actionState.primaryPkValues.length,
                        })
                      : translate("QMS/SELECTOR/ALL_RECORDS")}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Scope Mappings (from actionState) */}
          {actionState && Object.keys(actionState.scopeMappings).length > 0 && (
            <div className="mb-4">
              <h3 className="mb-2 text-sm font-medium text-grey6">
                {translate("QMS/SELECTOR/LINKED_SCOPES")}
              </h3>
              <div className="space-y-2">
                {Object.entries(actionState.scopeMappings).map(
                  ([sid, mapping]) => (
                    <div
                      key={sid}
                      className={clsx(
                        "flex items-center justify-between rounded-lg border px-3 py-2",
                        mapping.status === "removed"
                          ? "border-grey4/50 bg-grey3/50 opacity-50"
                          : mapping.status === "user-edited"
                            ? "border-purple1/30 bg-purple1/5"
                            : "border-grey4 bg-grey3",
                      )}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <span
                          className={clsx(
                            "rounded px-1.5 py-0.5 text-[10px] font-medium",
                            mapping.status === "user-edited"
                              ? "bg-purple1/20 text-purple1"
                              : mapping.status === "removed"
                                ? "bg-grey4 text-grey5"
                                : "bg-grey4 text-grey5",
                          )}
                        >
                          {mapping.status === "user-edited"
                            ? translate("QMS/SELECTOR/STATUS_MAPPED")
                            : mapping.status === "removed"
                              ? translate("QMS/SELECTOR/STATUS_EXCLUDED")
                              : translate("QMS/SELECTOR/STATUS_DEFAULT")}
                        </span>
                        <span className="text-xs font-medium capitalize text-white">
                          {scopeName(sid)}
                        </span>
                        {mapping.documentColumn != null && (
                          <span className="font-mono text-[10px] text-grey5">
                            col {mapping.documentColumn}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {/* Rollup/Rolldown placeholders */}
                        <button
                          disabled
                          className="cursor-not-allowed rounded border border-grey4 bg-grey3 px-1.5 py-0.5 text-[9px] text-grey5 opacity-40"
                          title={translate("QMS/SELECTOR/COMING_SOON")}
                        >
                          {translate("QMS/SELECTOR/AGGREGATION_BTN")}
                        </button>
                        <button
                          disabled
                          className="cursor-not-allowed rounded border border-grey4 bg-grey3 px-1.5 py-0.5 text-[9px] text-grey5 opacity-40"
                          title={translate("QMS/SELECTOR/COMING_SOON")}
                        >
                          {translate("QMS/SELECTOR/INHERITANCE_BTN")}
                        </button>
                        {mapping.status === "removed" ? (
                          <button
                            onClick={() => restoreScopeMapping(sid)}
                            className="rounded border border-yellow5 px-2 py-0.5 text-[10px] text-yellow1 transition-colors hover:bg-yellow5/10"
                          >
                            {translate("QMS/SELECTOR/RESTORE")}
                          </button>
                        ) : (
                          <button
                            onClick={() => removeScopeMapping(sid)}
                            className="text-grey5 transition-colors hover:text-red1"
                            title={translate("QMS/SELECTOR/EXCLUDE_SCOPE")}
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

          {/* Legacy PK Sets (when no actionState) */}
          {!actionState && (
            <div className="mb-4">
              <h3 className="mb-2 text-sm font-medium text-grey6">
                {translate("QMS/SELECTOR/RANGE_FILTER")}
              </h3>
              <div className="space-y-2">
                {pkSets.map((ps) => (
                  <div
                    key={ps.scopeId}
                    className="flex items-center justify-between rounded-lg border border-grey4 bg-grey3 px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-medium text-white">
                        {ps.scopeId}
                      </span>
                      <span className="ml-2 text-[10px] text-grey5">
                        {translate("QMS/SELECTOR/PKS_FROM_FILES", {
                          count: ps.pkValues.length,
                          files: ps.fileNames.join(", "),
                        })}
                      </span>
                    </div>
                    {pkSets.length > 1 && (
                      <button
                        onClick={() => removePkSet(ps.scopeId)}
                        className="ml-2 shrink-0 text-grey5 transition-colors hover:text-red1"
                        title={translate("QMS/SELECTOR/REMOVE_RANGE_FILTER")}
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cross-scope operator (only if multiple pkSets and no actionState) */}
          {!actionState && pkSets.length > 1 && (
            <div className="mb-4 rounded-lg border border-yellow5 bg-yellow5/20 p-3">
              <p className="mb-2 text-xs font-semibold text-yellow1">
                {translate("QMS/SELECTOR/CROSS_SCOPE")}
              </p>
              <div className="flex gap-3">
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-yellow1">
                  <input
                    type="radio"
                    checked={crossScopeOperator === "or"}
                    onChange={() => setCrossScopeOperator("or")}
                    className="accent-yellow1"
                  />
                  {translate("QMS/SELECTOR/OPERATOR_OR")}
                </label>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-yellow1">
                  <input
                    type="radio"
                    checked={crossScopeOperator === "and"}
                    onChange={() => setCrossScopeOperator("and")}
                    className="accent-yellow1"
                  />
                  {translate("QMS/SELECTOR/OPERATOR_AND")}
                </label>
              </div>
            </div>
          )}

          {/* Column visibility — scope tabs */}
          <div className="mb-4">
            <h3 className="mb-2 text-sm font-medium text-grey6">
              {translate("QMS/SELECTOR/VISIBLE_COLUMNS")}
            </h3>

            {/* Scope tabs */}
            <div className="flex items-end gap-0.5 border-b border-grey4">
              {scopeIds.map((sid) => {
                const scopeMappings = byScope.get(sid)!
                const scopeVisible = scopeMappings.filter(
                  (m) => m.visible,
                ).length
                const isActive = sid === effectiveTab
                return (
                  <button
                    key={sid}
                    onClick={() => setActiveTab(sid)}
                    className={clsx(
                      "flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2 text-xs font-medium transition-colors",
                      isActive
                        ? "border-yellow5 text-yellow1"
                        : "border-transparent text-grey5 hover:border-grey4 hover:text-white",
                    )}
                  >
                    <span>{scopeMappings[0]?.scopeName ?? sid}</span>
                    <span
                      className={clsx(
                        "text-[10px] tabular-nums",
                        isActive ? "text-yellow1" : "text-grey5",
                      )}
                    >
                      {scopeVisible}/{scopeMappings.length}
                    </span>
                  </button>
                )
              })}
            </div>

            {/* Column table for active tab */}
            <div className="max-h-[30vh] overflow-hidden overflow-y-auto rounded-b-lg border border-t-0 border-grey4">
              <table className="w-full text-xs">
                <thead>
                  <tr className="sticky top-0 border-b border-grey4 bg-grey3">
                    <th className="w-10 px-3 py-1.5 text-left font-medium text-grey5">
                      {translate("QMS/SELECTOR/SHOW_TH")}
                    </th>
                    <th className="px-3 py-1.5 text-left font-medium text-grey5">
                      {translate("QMS/SELECTOR/FIELD_TH")}
                    </th>
                    <th className="px-3 py-1.5 text-left font-medium text-grey5">
                      {translate("QMS/SELECTOR/FILE_COLUMN_TH")}
                    </th>
                    {effectiveTab !== activeScopeId && (
                      <th
                        className="w-16 px-3 py-1.5 text-left font-medium text-grey5"
                        title={translate("QMS/SELECTOR/AGGREGATION")}
                      >
                        {translate("QMS/SELECTOR/AGGREGATION")}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-grey4/50">
                  {tabMappings.map((m) => (
                    <tr
                      key={m.primitiveId}
                      className={clsx(
                        "transition-colors hover:bg-grey3",
                        !m.visible && "opacity-50",
                      )}
                    >
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={m.visible}
                          disabled={m.isPk}
                          onChange={() =>
                            toggleSelectionPrimitive(m.primitiveId)
                          }
                          className="rounded accent-yellow1"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <span className="font-medium text-white">
                          {m.primitiveTitle}
                        </span>
                        {m.isPk && (
                          <span className="ml-1.5 rounded bg-yellow1/15 px-1.5 py-0.5 text-[10px] font-medium text-yellow1">
                            PK
                          </span>
                        )}
                      </td>
                      <td className="font-mono px-3 py-2 text-grey5">
                        {m.header || "—"}
                      </td>
                      {effectiveTab !== activeScopeId && (
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={!!m.isRollup}
                            disabled={m.isPk}
                            onChange={() =>
                              toggleSelectionRollup(m.primitiveId)
                            }
                            className="rounded accent-yellow1"
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Near-miss warnings */}
          {nearMisses.length > 0 && (
            <div className="mb-4 overflow-hidden rounded-lg border border-warning/30 bg-warning/10">
              <button
                onClick={() => setNearMissExpanded((v) => !v)}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-warning/10"
              >
                <svg
                  className="h-4 w-4 shrink-0 text-warning"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span className="text-xs font-semibold text-warning">
                  {translate("QMS/SELECTOR/NEAR_MISS_COUNT", {
                    count: nearMisses.length,
                  })}
                </span>
                <svg
                  className={clsx(
                    "ml-auto h-3 w-3 text-warning transition-transform",
                    nearMissExpanded && "rotate-180",
                  )}
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M3 5l3 3 3-3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {nearMissExpanded && (
                <div className="px-4 pb-3">
                  <div className="overflow-hidden rounded border border-warning/30">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-warning/30 bg-warning/20">
                          <th className="px-3 py-1.5 text-left font-medium text-warning">
                            {translate("QMS/SELECTOR/DB_PK")}
                          </th>
                          <th className="px-3 py-1.5 text-left font-medium text-warning">
                            {translate("QMS/SELECTOR/DOC_PK")}
                          </th>
                          <th className="w-20 px-3 py-1.5 text-right font-medium text-warning">
                            {translate("QMS/SELECTOR/SIMILARITY")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-warning/10">
                        {nearMisses.map((w, i) => (
                          <tr key={i} className="hover:bg-warning/10">
                            <td className="font-mono px-3 py-1.5 text-warning">
                              {w.db_pk}
                            </td>
                            <td className="font-mono px-3 py-1.5 text-warning">
                              {w.doc_pk}
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <span
                                className={clsx(
                                  "inline-block rounded px-1.5 py-0.5 text-[10px] font-medium",
                                  w.score >= 0.9
                                    ? "bg-red1/10 text-red1"
                                    : "bg-warning/10 text-warning",
                                )}
                              >
                                {Math.round(w.score * 100)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-grey4 bg-grey3 px-6 py-4">
          <div className="flex gap-2">
            <button
              onClick={actionState ? cancelAction : clearRecordSelection}
              className="rounded-lg border border-red1/30 px-3 py-1.5 text-xs text-red1 transition-colors hover:bg-red1/10 hover:text-red1"
            >
              {actionState
                ? translate("QMS/SELECTOR/CANCEL_ACTION")
                : translate("QMS/SELECTOR/CLEAR_SELECTION")}
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="rounded-lg border border-yellow5 px-3 py-1.5 text-xs font-medium text-yellow1 transition-colors hover:bg-yellow5/30"
            >
              {translate("QMS/SELECTOR/ADD_FILE")}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.xlsm,.csv,.pdf,.txt"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleAddFile(file)
                e.target.value = ""
              }}
            />
            <button
              onClick={closeRecordSelectionEditor}
              className="rounded-lg bg-yellow1 px-4 py-1.5 text-xs font-medium text-black transition-colors hover:bg-yellow5"
            >
              {translate("QMS/COMMON/DONE")}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
